import { useContext, useEffect, useState } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import { useAdmin } from '../../../hooks/use-admin-context';

interface UseProductFruitsSetupResult {
  workspaceCode: string;
  shouldRender: boolean;
  user: {
    username: string;
    email: string;
    firstname: string;
  };
}

export const useProductFruitsSetup = (): UseProductFruitsSetupResult => {
  const { tenant } = useContext(TenantContext);
  const { email, name } = useAdmin();
  const [workspaceCode, setWorkspaceCode] = useState('');

  // Check if we're in development/local environment
  const isLocalEnvironment = process.env.NODE_ENV === 'development';

  // Parse the comma-separated list of tenants
  const activeTenants = process.env.REACT_APP_PRODUCT_FRUITS_TENANTS;
  const tenantList = activeTenants ? activeTenants.split(',').map((t) => t.trim()) : [];

  // Get the workspace code
  const code = process.env.REACT_APP_PRODUCT_FRUITS_WORKSPACE_CODE || '';

  // Don't render ProductFruits in QA and Dev environments
  const isQaOrDev = Boolean(
    tenant && ['sample-app', 'sample-app-qa', 'qa_ca', 'qa_aus', 'dev_ca', 'dev_aus'].includes(tenant),
  );

  // Check if current tenant is in the active list
  // If tenantList is empty, enable for all tenants
  const isTenantActive = Boolean(tenant && (tenantList.length === 0 || tenantList.includes(tenant)));

  // Validate workspace code
  const isValidWorkspaceCode = Boolean(code && code.length > 0);

  // Create user object
  const user = {
    username: tenant && email ? `${tenant}-${email}` : '',
    email: email || '',
    firstname: name || '',
  };

  useEffect(() => {
    // Don't render ProductFruits in local environments
    if (isLocalEnvironment || isQaOrDev) {
      return;
    }

    // Don't render if tenant is not active
    if (!isTenantActive) {
      return;
    }

    // Don't render if workspace code is not valid
    if (!isValidWorkspaceCode) {
      console.warn('ProductFruits workspace code is not configured');
      return;
    }

    console.log('setting workspace code', code);
    setWorkspaceCode(code);
  }, []);

  const shouldRender = workspaceCode.length > 0;

  return {
    workspaceCode,
    shouldRender,
    user,
  };
};
